/**
 * `catalog/context` — the inputs a caller gathers, and how they become a `ResolveContext`.
 *
 * 🔴 **This lived in `./posCard`, and did not belong there.** That file is the POS's LEGACY CARD
 * SHAPE and exists to be deleted; assembling a resolve context is neither legacy nor a card. The
 * coupling was not theoretical — `recommendations/selection` imported `posCard` for this and
 * nothing else, so its test had to stub the whole legacy module to exercise selection rules that
 * have no card in them, and moving the projection to the lean builder broke that stub.
 *
 * ## Why two context shapes exist at all
 *
 * `ResolveContext` (`./types`) holds a **pre-stamped `branchTime`**, so every card in one batch
 * is priced and schedule-checked against a single instant — a batch cannot straddle a boundary
 * partway through. `CatalogContextInput` holds a **zone id** instead, because that is what a
 * store actually contains, and stamps the instant once per call.
 *
 * So this is not a duplicate of `ResolveContext`; it is the un-stamped form, and
 * `toResolveContext` is the one place the clock is read.
 */
import { branchTimeOf } from "../time/branchClock";

import type { ResolveContext } from "./types";

/**
 * Catalogue inputs as a STORE holds them — zone id rather than an instant.
 *
 * ⚠️ Formerly `PosCatalogContext`. Renamed because the name had stopped being true: the kiosk's
 * suggestion rail reaches this through `recommendations/selection`, so "Pos" was describing the
 * first caller rather than the shape. Nothing about it is POS-specific — `useCatalogContext`
 * fills it from reducer keys both apps share.
 */
export interface CatalogContextInput {
  itemsData: Record<string, any>;
  selectedChannel: any;
  itemSchedules: any;
  branchId?: string;
  branchTimeZoneId?: string;
  soldOutItemsList?: string[];
}

/**
 * `CatalogContextInput` → the pipeline's `ResolveContext`.
 *
 * ⚠️ `branchTime` is stamped ONCE here from the common branch clock (`time/branchClock`), where the
 * retired projection called `tz(...).format()` per item. Same value in every practical case and
 * strictly more consistent. The clock falls back to the terminal's own when the business has no zone.
 */
export function toResolveContext(context: CatalogContextInput | undefined): ResolveContext {
  const ctx = context ?? ({} as CatalogContextInput);
  return {
    itemsById: ctx.itemsData,
    channel: ctx.selectedChannel,
    branchId: ctx.branchId as string,
    branchTime: branchTimeOf(ctx.branchTimeZoneId),
    itemSchedules: ctx.itemSchedules,
    soldOutItemIds: ctx.soldOutItemsList ?? [],
  };
}
